import React from "react";
import type { User } from "@domain/Auth";
import { LoginForm, Logout, Register, PasswordReset, RegistrationContactNotice } from "@gui/pages/Authentication";
import HomePage from "@gui/pages/Home/Home";
import type { RouteId } from "./routeTable";

// Lazy-loaded routes — split into separate chunks
const JobsDashboardWithLocalizationProvider = React.lazy(() => import("@gui/pages/JobsDashboard/JobsDashboard"));
const JobDetailPage = React.lazy(() =>
	import("@gui/pages/JobsDashboard/JobDetail").then((m) => ({ default: m.JobDetailPage })),
);
const JobSubmissionForm = React.lazy(() => import("@gui/pages/SubmitJobs/ImportForm"));
const MarkJobReadyPage = React.lazy(() =>
	import("@gui/pages/MarkJobsReady/MarkJobReadyPage").then((m) => ({ default: m.MarkJobReadyPage })),
);
const Profile = React.lazy(() => import("@gui/pages/Profile").then((m) => ({ default: m.Profile })));

// Admin pages — rarely visited, biggest code-splitting win
const AgrianIssues = React.lazy(() =>
	import("@gui/pages/Admin/AgrianIssues").then((m) => ({ default: m.AgrianIssues })),
);
const JobErrors = React.lazy(() => import("@gui/pages/Admin/JobErrors").then((m) => ({ default: m.JobErrors })));
const Skips = React.lazy(() => import("@gui/pages/Admin/Skips").then((m) => ({ default: m.Skips })));
const GeoFileConverter = React.lazy(() =>
	import("@gui/pages/Admin/GeoFileConverter").then((m) => ({ default: m.GeoFileConverter })),
);
const ImportSettings = React.lazy(() =>
	import("@gui/pages/Admin/ImportSettings").then((m) => ({ default: m.ImportSettings })),
);
const MissionControlLogs = React.lazy(() =>
	import("@gui/pages/Admin/MissionControlLogs").then((m) => ({ default: m.MissionControlLogs })),
);
const MissionReport = React.lazy(() =>
	import("@gui/pages/Admin/MissionReport").then((m) => ({ default: m.MissionReport })),
);
const Passwords = React.lazy(() => import("@gui/pages/Admin/Passwords").then((m) => ({ default: m.Passwords })));
const Robots = React.lazy(() => import("@gui/pages/Admin/Robots").then((m) => ({ default: m.Robots })));
const DealCreator = React.lazy(() => import("@gui/pages/Admin/DealCreator").then((m) => ({ default: m.DealCreator })));
const DealMatcher = React.lazy(() => import("@gui/pages/Admin/DealMatcher").then((m) => ({ default: m.DealMatcher })));
const BranchMatcher = React.lazy(() =>
	import("@gui/pages/Admin/BranchMatcher").then((m) => ({ default: m.BranchMatcher })),
);
const Scheduling = React.lazy(() => import("@gui/pages/Admin/Scheduling").then((m) => ({ default: m.Scheduling })));

export interface RouteElementProps {
	/** Login / Register / PasswordReset / Home hand a freshly authenticated user up to `App`. */
	onLogin: (user: User) => void;
	/** The dashboard and Mark-Ready pages drive `App`'s full-screen loader during their fetches. */
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/** The page each registry entry renders. Typed by `RouteId`, so an entry without an element (or
 *  an element without an entry) fails `tsc`. */
export function routeElements({ onLogin, setLoading }: RouteElementProps): Record<RouteId, React.ReactNode> {
	return {
		root: <LoginForm setLoggedIn={onLogin} />,
		login: <LoginForm setLoggedIn={onLogin} />,
		register: <Register setLoggedIn={onLogin} />,
		registrationNotice: <RegistrationContactNotice />,
		forgotPassword: <PasswordReset setLoggedIn={onLogin} />,
		resetPassword: <PasswordReset setLoggedIn={onLogin} />,
		logout: <Logout />,
		home: <HomePage setLoggedIn={onLogin} />,
		profile: <Profile />,
		jobsDashboard: <JobsDashboardWithLocalizationProvider setLoading={setLoading} />,
		submitJobs: <JobSubmissionForm />,
		markJobsReady: <MarkJobReadyPage setLoading={setLoading} />,
		jobDetail: <JobDetailPage />,
		robots: <Robots />,
		scheduling: <Scheduling />,
		jobErrors: <JobErrors />,
		skips: <Skips />,
		dealMatcher: <DealMatcher />,
		branchMatcher: <BranchMatcher />,
		passwords: <Passwords />,
		dealCreator: <DealCreator />,
		companySettings: <ImportSettings />,
		geoFileConverter: <GeoFileConverter />,
		missionReport: <MissionReport />,
		missionControlLogs: <MissionControlLogs />,
		agrianIssues: <AgrianIssues />,
	};
}
